import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BrandMark } from "../components/Brand";
import TrustCues from "../components/TrustCues";
import { useSEO } from "../lib/useSEO";
import { buildEmergentLoginUrl } from "../lib/attorneyAuth";
import { api, formatApiError } from "../lib/api";
import { Scale, ArrowRight, ShieldCheck, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AttorneyLogin() {
  useSEO("Attorney sign in", "Sign in with Google or email-link to access your firm's Instabridge funding portal.");
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || "";
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/invitations/${inviteToken}`);
        setInvite(data);
        if (data?.email) setMagicEmail(data.email);
      } catch (e) {
        setInviteError(formatApiError(e));
      }
    })();
  }, [inviteToken]);

  const startOAuth = () => {
    window.location.href = buildEmergentLoginUrl();
  };

  const sendMagicLink = async (e) => {
    e.preventDefault();
    if (!magicEmail) return;
    setMagicSending(true);
    try {
      await api.post("/attorney/auth/magic-link/request", { email: magicEmail });
      setMagicSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setMagicSending(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="attorney-login-page">
      <div className="hidden lg:flex flex-col justify-between bg-[#0A2540] text-white p-12 relative overflow-hidden">
        <BrandMark inverted />
        <div className="relative z-10">
          <div className="uppercase-label !text-white/60 mb-4 flex items-center gap-2"><Scale className="w-3.5 h-3.5" /> Attorney portal</div>
          <h2 className="font-display text-4xl xl:text-5xl font-light tracking-tight leading-tight">
            All your cases.<br /><span className="font-semibold">One dashboard.</span>
          </h2>
          <p className="mt-6 text-white/70 max-w-md leading-relaxed">
            Track every active funding case for your firm. Status, amounts, payoff letters, documents — all in one place.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-white/80">
            {["Real-time case status", "Pull payoff letters anytime", "Re-upload documents on demand", "Direct line to underwriting"].map((x) => (
              <li key={x} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF]" /> {x}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-xs text-white/50">© {new Date().getFullYear()} Instabridge Funding · No passwords. Ever.</div>
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-[#0066FF]/30 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md space-y-6" data-testid="attorney-login-form">
          <div className="lg:hidden mb-4"><BrandMark /></div>

          <div>
            <h1 className="font-display text-3xl text-[#0A2540] font-semibold tracking-tight">Attorney sign in</h1>
            <p className="text-[#6B7280] mt-1.5 text-sm">Pick your preferred sign-in below — works with Gmail, Outlook, or any firm email.</p>
          </div>

          {invite && (
            <div className="rounded-xl bg-[#EFF6FF] border border-[#DBEAFE] px-4 py-3 text-sm text-[#0A2540]" data-testid="invite-banner">
              You've been invited to the Instabridge attorney portal{invite.firm ? ` for ${invite.firm}` : ""}. Sign in below with <span className="font-medium">{invite.email}</span> to activate your access.
            </div>
          )}
          {inviteError && (
            <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-[#B91C1C]" data-testid="invite-error">
              {inviteError}
            </div>
          )}

          {/* Google OAuth — fastest path for Gmail / Google Workspace firms */}
          <Button onClick={startOAuth} className="w-full h-12 rounded-lg bg-white border border-[#E5E7EB] hover:border-[#0066FF] hover:bg-[#F9FAFB] text-[#0A2540] font-medium gap-3" data-testid="google-signin-btn">
            <GoogleG />
            Continue with Google
            <ArrowRight className="w-4 h-4 ml-1 text-[#0066FF]" />
          </Button>

          {/* Divider */}
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#9CA3AF]">
            <div className="flex-1 border-t border-[#E5E7EB]" /> or <div className="flex-1 border-t border-[#E5E7EB]" />
          </div>

          {/* Magic-link — works for ANY email provider including Outlook / M365 */}
          {magicSent ? (
            <div className="rounded-xl bg-[#ECFDF5] border border-[#A7F3D0] px-4 py-5 text-center" data-testid="magic-sent-card">
              <CheckCircle2 className="w-7 h-7 text-[#047857] mx-auto" />
              <div className="font-medium text-[#0A2540] mt-2">Check your inbox</div>
              <p className="text-sm text-[#374151] mt-1.5 leading-relaxed">
                We sent a secure sign-in link to <span className="font-medium">{magicEmail}</span>. It expires in 15 minutes.
              </p>
              <button onClick={() => { setMagicSent(false); }} className="text-xs text-[#0066FF] hover:underline mt-3" data-testid="magic-resend-link">
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={sendMagicLink} className="space-y-3" data-testid="magic-form">
              <Label htmlFor="magic-email" className="text-sm">Sign in with email link <span className="text-[#9CA3AF]">(Outlook · Gmail · firm address)</span></Label>
              <div className="flex gap-2">
                <Input id="magic-email" type="email" value={magicEmail} onChange={(e) => setMagicEmail(e.target.value)} placeholder="you@yourfirm.com" required data-testid="magic-email-input" className="flex-1" />
                <Button type="submit" disabled={magicSending} className="btn-primary-navy h-10 px-4" data-testid="magic-submit-btn">
                  {magicSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-[#6B7280]">No password needed. We'll email you a one-time link that signs you in for 7 days.</p>
            </form>
          )}

          <div className="flex items-center gap-2 text-xs text-[#6B7280]">
            <ShieldCheck className="w-3.5 h-3.5 text-[#047857]" />
            Secured by httpOnly session cookies. Never share your sign-in link.
          </div>

          <TrustCues variant="trust-line" audience="attorney" align="center" testid="attorney-login-trust" />

          <div className="border-t border-[#E5E7EB] pt-5 text-sm text-[#6B7280] space-y-2">
            <div>Don't have access yet? <Link to="/contact" className="text-[#0066FF] hover:underline">Talk to our team →</Link></div>
            <div className="flex items-center justify-between">
              <Link to="/" className="hover:text-[#0A2540]">← Back to homepage</Link>
              <Link to="/login" className="hover:text-[#0A2540]">Underwriter sign in →</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.616z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}
